import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { seekerDataService } from '../../services/seekerDataService';
import { OnboardingAnswers, QuestionDefinition } from '../../types/onboarding';
import { onboardingSections } from '../../onboarding/sections';
import { getSupabaseClient } from '../../lib/supabase';

// Configuration for "Other" text fields
const OPTION_OTHER_FIELD_CONFIG: Record<string, { answerId: string; label: string }> = {
  experience_types: {
    answerId: 'experience_types_other',
    label: 'If Other, please specify'
  },
  seeker_beliefs: {
    answerId: 'seeker_beliefs_other_text',
    label: 'If Other, please specify'
  }
};

const GROUP_OPTION_OTHER_FIELD_CONFIG: Record<string, { answerId: string; label: string }> = {
  focus_special_pref: {
    answerId: 'focus_special_other_text',
    label: 'If Other, please specify'
  }
};

const isOtherSelected = (value: string | string[] | undefined): boolean => {
  if (Array.isArray(value)) {
    return value.some(option => typeof option === 'string' && option.trim().toLowerCase() === 'other');
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'other';
  }
  return false;
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}

export const EditProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<OnboardingAnswers>({});
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await seekerDataService.getOnboardingEditData();

      if (!data) {
        setError('No profile data found. Please complete onboarding first.');
        return;
      }

      setFormData(data.answers);
      setProfileImageUrl(data.imageUrl ?? null);
      setPhotoPreview(null);
    } catch (err) {
      console.error('Failed to load profile data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (questionId: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
    setSaveSuccess(false);
  };

  const handleGroupedChange = (groupId: string, value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [groupId]: value
    }));
    setSaveSuccess(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await seekerDataService.updateOnboardingAndProfile(formData);
      setSaveSuccess(true);
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to save profile');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handlePickPhoto = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) {
      return;
    }

    setPhotoError(null);

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setPhotoError('Please choose a JPG, PNG, or WEBP image.');
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setPhotoError('Image must be 5MB or smaller.');
      return;
    }

    const supabase = getSupabaseClient();
    const env = (window as any).__ENV__;

    if (!env?.SUPABASE_URL) {
      setPhotoError('Missing environment configuration. Please refresh and try again.');
      return;
    }

    try {
      setUploadingPhoto(true);
      setSaveSuccess(false);

      const base64Preview = await fileToBase64(file);
      setPhotoPreview(base64Preview);

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('You need to be signed in to upload a photo.');
      }

      const response = await fetch(`${env.SUPABASE_URL}/functions/v1/seeker-profile-photo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          image: base64Preview,
          mimeType: file.type,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error ?? 'Failed to upload photo.');
      }

      setProfileImageUrl(result.imageUrl ?? null);
      setPhotoPreview(null);
      setSaveSuccess(true);
    } catch (uploadError) {
      console.error('Failed to upload seeker photo:', uploadError);
      setPhotoError(uploadError instanceof Error ? uploadError.message : 'Unable to upload photo.');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    const supabase = getSupabaseClient();
    try {
      setUploadingPhoto(true);
      setPhotoError(null);
      setSaveSuccess(false);

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('You need to be signed in to update your photo.');
      }

      const { error: updateError } = await supabase
        .from('seekers')
        .update({ image_url: null })
        .eq('user_id', user.id);

      if (updateError) {
        throw new Error(updateError.message);
      }

      setProfileImageUrl(null);
      setPhotoPreview(null);
      setSaveSuccess(true);
    } catch (removeError) {
      console.error('Failed to remove seeker photo:', removeError);
      setPhotoError(removeError instanceof Error ? removeError.message : 'Unable to remove photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const currentPhoto = photoPreview ?? profileImageUrl ?? null;

  const renderQuestion = (question: QuestionDefinition) => {
    const value = formData[question.id];
    const optionOtherConfig = OPTION_OTHER_FIELD_CONFIG[question.id];
    const otherFieldValue = optionOtherConfig && typeof formData[optionOtherConfig.answerId] === 'string'
      ? (formData[optionOtherConfig.answerId] as string)
      : '';
    const shouldShowOtherField = optionOtherConfig && isOtherSelected(value);

    switch (question.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <input
            id={question.id}
            type={question.type}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className="w-full rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]"
            placeholder={question.label}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={question.id}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className="w-full rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]"
            rows={4}
            placeholder={question.label}
          />
        );

      case 'singleselect':
        return (
          <>
            <div className="space-y-2">
              {question.options?.map(option => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                return (
                  <label
                    key={optionValue}
                    className="flex items-center rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] p-3 text-sm text-[#2C2731] transition hover:bg-[#F5ECC5]"
                  >
                    <input
                      type="radio"
                      name={question.id}
                      value={optionValue}
                      checked={value === optionValue}
                      onChange={(e) => handleChange(question.id, e.target.value)}
                      className="mr-3 h-4 w-4 text-[#2C2731] focus:ring-[#2C2731]"
                    />
                    <span>{optionLabel}</span>
                  </label>
                );
              })}
            </div>
            {shouldShowOtherField && (
              <div className="mt-3">
                <label className="mb-2 block text-sm font-medium text-[#2C2731]">
                  {optionOtherConfig.label}
                </label>
                <input
                  type="text"
                  value={otherFieldValue}
                  onChange={(e) => handleChange(optionOtherConfig.answerId, e.target.value)}
                  className="w-full rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]"
                  placeholder="Please specify"
                />
              </div>
            )}
          </>
        );

      case 'multiselect':
        return (
          <>
            <div className="space-y-2">
              {question.options?.map(option => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const selected = Array.isArray(value) && value.includes(optionValue);
                return (
                  <label
                    key={optionValue}
                    className="flex items-center rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] p-3 text-sm text-[#2C2731] transition hover:bg-[#F5ECC5]"
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {
                        const current = Array.isArray(value) ? [...value] : [];
                        if (selected) {
                          handleChange(question.id, current.filter(item => item !== optionValue));
                        } else {
                          handleChange(question.id, [...current, optionValue]);
                        }
                      }}
                      className="mr-3 h-4 w-4 text-[#2C2731] focus:ring-[#2C2731]"
                    />
                    <span>{optionLabel}</span>
                  </label>
                );
              })}
            </div>
            {shouldShowOtherField && (
              <div className="mt-3">
                <label className="mb-2 block text-sm font-medium text-[#2C2731]">
                  {optionOtherConfig.label}
                </label>
                <input
                  type="text"
                  value={otherFieldValue}
                  onChange={(e) => handleChange(optionOtherConfig.answerId, e.target.value)}
                  className="w-full rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]"
                  placeholder="Please specify"
                />
              </div>
            )}
          </>
        );

      case 'grouped-multiselect':
        return (
          <div className="space-y-6">
            {question.groups?.map((group) => {
              const groupValue = formData[group.id];
              const groupOtherConfig = GROUP_OPTION_OTHER_FIELD_CONFIG[group.id];
              const groupOtherValue = groupOtherConfig && typeof formData[groupOtherConfig.answerId] === 'string'
                ? (formData[groupOtherConfig.answerId] as string)
                : '';
              const shouldShowGroupOther = groupOtherConfig && isOtherSelected(groupValue);

              return (
                <div key={group.id} className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-4 shadow-sm">
                  <h4 className="mb-3 text-sm font-semibold text-[#2C2731]">{group.label}</h4>
                  <div className="space-y-2">
                    {group.options.map(option => {
                      const optionValue = getOptionValue(option);
                      const optionLabel = getOptionLabel(option);
                      const selected = Array.isArray(groupValue) && groupValue.includes(optionValue);
                      return (
                        <label
                          key={optionValue}
                          className="flex items-center rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] p-2 text-sm text-[#2C2731] transition hover:bg-[#F5ECC5]"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const current = Array.isArray(groupValue) ? [...groupValue] : [];
                              if (selected) {
                                handleGroupedChange(group.id, current.filter(item => item !== optionValue));
                              } else {
                                handleGroupedChange(group.id, [...current, optionValue]);
                              }
                            }}
                            className="mr-3 h-4 w-4 text-[#2C2731] focus:ring-[#2C2731]"
                          />
                          <span>{optionLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                  {shouldShowGroupOther && (
                    <div className="mt-3">
                      <label className="mb-2 block text-sm font-medium text-[#2C2731]">
                        {groupOtherConfig.label}
                      </label>
                      <input
                        type="text"
                        value={groupOtherValue}
                        onChange={(e) => handleChange(groupOtherConfig.answerId, e.target.value)}
                        className="w-full rounded-lg border border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]"
                        placeholder="Please specify"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFCEF]">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
          <p className="text-sm text-[#2C2731]/70">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error && !formData.seeker_email) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFCEF]">
        <div className="max-w-md rounded-2xl border border-[#2C2731] bg-[#FFFCEF] px-6 py-8 text-center shadow-sm">
          <p className="mb-4 text-sm text-red-600">{error}</p>
          <button
            onClick={() => navigate('/profile')}
            className="w-full rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFCEF]">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-[#FFFCEF]/20 bg-[#2C2731] text-[#FFFCEF]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/profile')}
            className="rounded-full p-2 hover:bg-[#FFFCEF]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731]"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-base font-semibold">Edit Profile & Preferences</span>
          <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-6">
        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 rounded-2xl border border-[#2C2731] bg-[#F5ECC5] px-4 py-3 shadow-sm">
            <p className="text-sm font-semibold text-[#2C2731]">Profile updated successfully!</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-2xl border border-red-300 bg-[#FBE2E2] px-4 py-3 shadow-sm">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSave}>
          <div className="mb-8 rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-[#2C2731]">Profile Photo</h2>
            <p className="mt-1 text-sm text-[#2C2731]/70">
              Add a photo so guides recognize you during introductions.
            </p>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/30 bg-[#F5ECC5]">
                {currentPhoto ? (
                  <img src={currentPhoto} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-sm text-[#2C2731]/60">No photo</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handlePickPhoto}
                    disabled={uploadingPhoto}
                    className="inline-flex items-center justify-center rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-4 py-2 text-sm font-semibold text-[#2C2731] transition hover:bg-[#F5ECC5] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {uploadingPhoto ? 'Uploading…' : 'Upload Photo'}
                  </button>
                  {currentPhoto && (
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      disabled={uploadingPhoto}
                      className="text-sm font-medium text-[#2C2731]/70 underline underline-offset-2 transition hover:text-[#2C2731] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#2C2731]/60">
                  JPG, PNG, or WEBP. Max size 5 MB.
                </p>
                {photoError && <p className="text-sm text-red-600">{photoError}</p>}
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handlePhotoSelected}
            />
          </div>

          {/* Sections */}
          {onboardingSections.map((section, sectionIndex) => (
            <div key={section.id} className="mb-8">
              <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
                <div className="mb-6 flex items-start">
                  <div className="mr-4 mt-1 text-[#2C2731]">{section.icon}</div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-[#2C2731]">{section.title}</h2>
                    {section.description && (
                      <p className="mt-1 text-sm text-[#2C2731]/70">{section.description}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  {section.questions.map((question) => {
                    // Skip the age question - it cannot be edited
                    if (question.id === 'seeker_over_18') {
                      return null;
                    }

                    // Skip conditional questions that shouldn't be shown
                    if (question.id === 'seeker_over_18') {
                      return null;
                    }
                    if (question.showIf) {
                      const { questionId, equals, includes } = question.showIf;
                      const dependentValue = formData[questionId];

                      if (equals !== undefined && dependentValue !== equals) {
                        return null;
                      }

                      if (includes !== undefined) {
                        if (!Array.isArray(dependentValue) || !dependentValue.includes(includes)) {
                          return null;
                        }
                      }
                    }

                    return (
                      <div key={question.id} className="space-y-2">
                        <label htmlFor={question.id} className="block text-sm font-medium text-[#2C2731]">
                          {question.label}
                          {question.required && <span className="text-red-600 ml-1">*</span>}
                        </label>
                        {question.helper && (
                          <p className="mb-2 text-xs text-[#2C2731]/60">{question.helper}</p>
                        )}
                        {renderQuestion(question)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}

          {/* Save Button */}
          <div className="sticky bottom-0 border-t border-[#2C2731]/20 bg-[#FFFCEF] px-4 py-4 shadow-lg shadow-[#2C2731]/15">
            <div className="mx-auto flex max-w-4xl gap-3">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex-1 rounded-lg border border-[#2C2731] px-6 py-3 text-sm font-semibold text-[#2C2731] transition hover:bg-[#F5ECC5]"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 rounded-lg bg-[#2C2731] px-6 py-3 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924] disabled:cursor-not-allowed disabled:opacity-70"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
const getOptionValue = (option: string | { value: string; label: string }) =>
  typeof option === 'string' ? option : option.value;

const getOptionLabel = (option: string | { value: string; label: string }) =>
  typeof option === 'string' ? option : option.label;
